import { App, URLOpenListenerEvent } from "@capacitor/app";
import { Browser } from "@capacitor/browser";
import { PluginListenerHandle } from "@capacitor/core";
import * as Sentry from "@sentry/react";
import Keycloak, { KeycloakAdapter } from "keycloak-js";
import React from "react";
import { SplashScreen } from "../../common/SplashScreen.js";
import { AuthContext } from "./AuthContext.js";
import { keycloakCapacitorTokenStorage } from "./keycloakCapacitorTokenStorage.js";

interface Props extends React.PropsWithChildren<{}> {
  keycloak: Keycloak;
}

export const AuthCapacitorProvider = ({ children, keycloak }: Props) => {
  const [isReady, setIsReady] = React.useState(false);
  const [isAuthenticated, setIsAuthenticated] = React.useState(false);

  React.useEffect(() => {
    function setIsAuthenticatedState() {
      setIsAuthenticated(keycloak.authenticated ?? false);
    }

    function persistTokens() {
      if (keycloak.token && keycloak.refreshToken) {
        void keycloakCapacitorTokenStorage.write({
          token: keycloak.token,
          refreshToken: keycloak.refreshToken,
          idToken: keycloak.idToken,
        });
      }
    }

    function clearTokens() {
      void keycloakCapacitorTokenStorage.clear();
    }

    async function bootstrap() {
      try {
        keycloak.onReady = () => {
          setIsReady(true);
        };
        keycloak.onAuthError = () => {
          setIsAuthenticatedState();
        };
        keycloak.onAuthLogout = () => {
          clearTokens();
          setIsAuthenticatedState();
        };
        keycloak.onAuthRefreshError = () => {
          clearTokens();
          setIsAuthenticatedState();
        };
        keycloak.onTokenExpired = () => {
          // Rotate the access token using the refresh token; the resulting
          // onAuthRefreshSuccess / onAuthRefreshError event drives state and
          // persistence. Without this, an expired access token would never be
          // refreshed and the user would be silently logged out.
          keycloak.updateToken(5).catch(e => {
            clearTokens();
            Sentry.captureException(e);
          });
        };
        keycloak.onAuthSuccess = () => {
          persistTokens();
          setIsAuthenticatedState();
        };
        keycloak.onAuthRefreshSuccess = () => {
          persistTokens();
          setIsAuthenticatedState();
        };

        const storedTokens = await keycloakCapacitorTokenStorage.read();

        await keycloak.init({
          // reference: https://github.com/jy95/keycloak-capacitor/blob/e0b3b4095826864e4d72f5cd061a20bce2a9341f/src/keycloak.js#L75
          adapter: getKeycloakCapacitorAdapter(keycloak),
          checkLoginIframe: false,
          responseMode: "query",
          onLoad: "check-sso",
          // flow: "hybrid", // 'implicit', 'hybrid',
          redirectUri: "de.meistersystems:/keycloak",
          token: storedTokens?.token,
          refreshToken: storedTokens?.refreshToken,
          idToken: storedTokens?.idToken,
        });
      } catch (e) {
        if (e instanceof Error)
          console.error("Failed to initialize keycloak: ", e.message);
        Sentry.captureException(e);
      }
    }

    void bootstrap();
  }, [keycloak]);

  if (!isReady) return <SplashScreen />;

  return (
    <AuthContext.Provider
      value={{
        isReady,
        isAuthenticated,
        belongsToGroup: (groupName: string) =>
          keycloak.idTokenParsed?.groups
            ? keycloak.idTokenParsed.groups.includes(groupName)
            : false,
        auth: keycloak,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

interface Deferred<T> {
  promise: Promise<T>;
  resolve: (value: T) => void;
  reject: (error: Error) => void;
}

// keycloak-js 26 dropped the internal `keycloak.createPromise` helper (it uses
// native promises everywhere), so the adapter builds its own deferred to bridge
// the Capacitor deep-link callback into the promise the adapter method returns.
function createDeferred<T>(): Deferred<T> {
  let resolve!: (value: T) => void;
  let reject!: (error: Error) => void;
  const promise = new Promise<T>((res, rej) => {
    resolve = res;
    reject = rej;
  });
  return { promise, resolve, reject };
}

function toError(e: unknown): Error {
  return e instanceof Error ? e : new Error(String(e));
}

async function openBrowserWithCallback(
  deferred: Deferred<void>,
  url: string,
  onDataCallback: (data: URLOpenListenerEvent) => void | Promise<void>
) {
  let addUrlListener: PluginListenerHandle | undefined;

  void deferred.promise.finally(() => {
    // Best-effort teardown — swallow rejections so a failed listener removal
    // or browser close does not surface as an unhandled promise rejection.
    void addUrlListener?.remove().catch(() => undefined);
    void Browser.close().catch(() => undefined);
  });

  try {
    // Registering the deep-link listener can itself reject (plugin
    // unavailable / init failure); keep it inside the try so a failure
    // rejects the deferred instead of hanging the fire-and-forget caller.
    addUrlListener = await App.addListener("appUrlOpen", onDataCallback);
    await Browser.open({ url, presentationStyle: "popover" });
  } catch (e) {
    const error = toError(e);
    Sentry.withScope(scope => {
      scope.setTag("service", "Capacitor");
      scope.setExtra("method", "openBrowserWithCallback");
      scope.setExtra("url", urlPathForSentry(url));
      Sentry.captureException(error);
    });
    // Always reject — a failed listener registration or a non-Error
    // rejection must not leave the adapter promise (and the browser/
    // listener) hanging.
    deferred.reject(error);
  }
}

// Strip query/hash from auth URLs before attaching to Sentry — they carry
// session state (state/nonce) and redirect params. Origin + path is enough.
function urlPathForSentry(rawUrl: string): string {
  try {
    const parsed = new URL(rawUrl);
    return `${parsed.origin}${parsed.pathname}`;
  } catch {
    return "[unparsable-url]";
  }
}

async function openBrowser(deferred: Deferred<void>, url: string) {
  try {
    await Browser.open({ url, presentationStyle: "popover" });
    deferred.resolve(undefined);
  } catch (e) {
    const error = toError(e);
    Sentry.withScope(scope => {
      scope.setTag("service", "Capacitor");
      scope.setExtra("method", "openBrowser");
      scope.setExtra("url", urlPathForSentry(url));
      Sentry.captureException(error);
    });
    deferred.reject(error);
  }
}

// references: https://github.com/edgeflare/keycloak-angular-capacitor/blob/master/projects/keycloak-angular-capacitor/src/lib/keycloak-angular-capacitor.service.ts
// taken from: https://github.com/jy95/keycloak-capacitor/blob/e0b3b4095826864e4d72f5cd061a20bce2a9341f/src/keycloak.js#L1668
const getKeycloakCapacitorAdapter = (keycloak: Keycloak): KeycloakAdapter => ({
  login: async options => {
    const deferred = createDeferred<void>();
    // createLoginUrl is async in keycloak-js 26 (stores the PKCE verifier).
    const loginUrl = await keycloak.createLoginUrl(options);

    void openBrowserWithCallback(deferred, loginUrl, async data => {
      try {
        const oauth = keycloak.parseCallback(data.url);
        await keycloak.processCallback(oauth);
        deferred.resolve(undefined);
      } catch (e) {
        deferred.reject(toError(e));
      }
    });

    return deferred.promise;
  },

  logout: options => {
    const deferred = createDeferred<void>();
    const logoutUrl = keycloak.createLogoutUrl(options);

    void openBrowserWithCallback(deferred, logoutUrl, () => {
      keycloak.clearToken();
      deferred.resolve(undefined);
    });

    return deferred.promise;
  },

  register: async options => {
    const deferred = createDeferred<void>();
    // createRegisterUrl is async in keycloak-js 26 (stores the PKCE verifier).
    const registerUrl = await keycloak.createRegisterUrl(options);

    void openBrowserWithCallback(deferred, registerUrl, async data => {
      try {
        const oauth = keycloak.parseCallback(data.url);
        await keycloak.processCallback(oauth);
        deferred.resolve(undefined);
      } catch (e) {
        deferred.reject(toError(e));
      }
    });

    return deferred.promise;
  },

  accountManagement: () => {
    const deferred = createDeferred<void>();
    const accountUrl = keycloak.createAccountUrl();
    if (!accountUrl) {
      throw new Error("Not supported by the OIDC server");
    }

    void openBrowser(deferred, accountUrl);

    return deferred.promise;
  },

  redirectUri: options => {
    if (options && options.redirectUri) {
      return options.redirectUri;
    } else if (keycloak.redirectUri) {
      return keycloak.redirectUri;
    } else {
      return "http://localhost";
    }
  },
});
